import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, VaultNode } from './api';

const KIND_ICON: Record<string, string> = {
  doc: '📄', file: '📎', kb_entry: '📚', diagram: '🗺️', excerpt: '💬', canvas: '📝',
};

export function WikiLinkOverlay({
  editorEl,
  workspaceId,
}: {
  editorEl: HTMLElement | null;
  workspaceId: string;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VaultNode[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [selected, setSelected] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const fn = q.length >= 1
        ? api.vaultNodes(workspaceId, undefined, q)
        : api.vaultNodes(workspaceId);
      fn.then(setResults).catch(() => {});
    }, 150);
  }, [workspaceId]);

  useEffect(() => {
    if (!editorEl) return;

    const handleInput = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const textNode = range.startContainer;
      if (textNode.nodeType !== Node.TEXT_NODE) return;
      const text = textNode.textContent || '';
      const caret = range.startOffset;
      const before = text.slice(0, caret);
      const match = before.match(/\[\[([^\]]{0,60})$/);

      if (match) {
        const q = match[1].toLowerCase();
        setQuery(q);
        setSelected(0);
        search(q);

        const rect = range.getBoundingClientRect();
        // Position relative to the overlay's positioned ancestor (.fx-docs-editor-wrap),
        // not the editor: the wrap also contains the toolbar above the editor, and
        // using its rect keeps the popup correct as the doc scrolls.
        const wrapEl = (editorEl.closest('.fx-docs-editor-wrap') as HTMLElement) || editorEl;
        const wrapRect = wrapEl.getBoundingClientRect();
        const PANEL_W = 280;
        setPos({
          top: rect.bottom - wrapRect.top + 4,
          left: Math.max(0, Math.min(rect.left - wrapRect.left, wrapRect.width - PANEL_W - 8)),
        });
        setOpen(true);
      } else if (open) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, Math.min(results.length - 1, 7)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (results.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          insertLink(results[selected]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };

    editorEl.addEventListener('input', handleInput);
    editorEl.addEventListener('keydown', handleKeyDown, true);
    return () => {
      editorEl.removeEventListener('input', handleInput);
      editorEl.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editorEl, open, results, selected, search]);

  const insertLink = useCallback((node: VaultNode) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) return;

    const text = textNode.textContent || '';
    const caret = range.startOffset;
    const before = text.slice(0, caret);
    const bracketStart = before.lastIndexOf('[[');
    if (bracketStart === -1) return;

    const linkText = `[[${node.title}]]`;
    const newText = text.slice(0, bracketStart) + linkText + text.slice(caret);
    textNode.textContent = newText;

    const newRange = document.createRange();
    const newPos = bracketStart + linkText.length;
    newRange.setStart(textNode, Math.min(newPos, newText.length));
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    textNode.parentElement?.dispatchEvent(new Event('input', { bubbles: true }));
    setOpen(false);
  }, []);

  useEffect(() => {
    // Keep the highlighted option in view while arrowing through a long list.
    panelRef.current
      ?.querySelector('.fx-wiki-item.is-selected')
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open || !editorEl) return null;

  return (
    <div
      ref={panelRef}
      className="fx-wiki-overlay"
      style={{ top: pos.top, left: pos.left }}
      onMouseDown={(e) => e.preventDefault()}
      role="listbox"
      aria-label="Link a doc"
    >
      {results.length === 0 && (
        <div className="fx-wiki-empty">
          {query ? 'No matching docs' : 'Type to search vault...'}
        </div>
      )}
      {results.slice(0, 8).map((n, i) => (
        <button
          type="button"
          key={n.id}
          className={`fx-wiki-item${i === selected ? ' is-selected' : ''}`}
          role="option"
          aria-selected={i === selected}
          onClick={() => insertLink(n)}
        >
          <span className="fx-wiki-icon">{KIND_ICON[n.kind] || '📌'}</span>
          <span className="fx-wiki-title">{n.title}</span>
          <span className="fx-wiki-kind">{n.kind}</span>
        </button>
      ))}
    </div>
  );
}
